import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Bell,
  Bot,
  CornerDownLeft,
  Mic,
  Plane,
  Power,
  Search,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFleet } from '@/store/fleet';
import { useSelection } from '@/store/selection';

/**
 * Cmd+K command palette. Searches across:
 *  - workspaces (every IconRail item)
 *  - fleet drones (focuses on Pilot)
 *  - global actions (emergency, sign out, settings shortcut)
 *
 * The fuzzy match is intentionally simple — substring scoring on a
 * concatenated haystack. Good enough for ~50 commands.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const robots = useFleet((s) => s.robots);
  const select = useSelection((s) => s.select);

  const items = useMemo<CommandItem[]>(() => {
    const workspaces: CommandItem[] = [
      { id: 'goto:/', label: 'Open Pilot', hint: 'Live ops · map · sticks', kind: 'workspace', icon: Plane, run: () => navigate({ to: '/' }) },
      { id: 'goto:/assistant', label: 'Open Assistant', hint: 'LLM · proposed actions', kind: 'workspace', icon: Bot, run: () => navigate({ to: '/assistant' }) },
    ];
    const droneItems: CommandItem[] = Object.values(robots).map((r) => ({
      id: `focus:${r.id}`,
      label: `Focus ${r.name}`,
      hint: `${r.id} · open Pilot`,
      kind: 'fleet',
      icon: Plane,
      run: () => {
        select(r.id);
        navigate({ to: '/' });
      },
    }));
    const actions: CommandItem[] = [
      { id: 'act:estop', label: 'Emergency stop · open dialog', hint: 'RTL / Land / Disarm', kind: 'action', icon: Power, run: () => { window.dispatchEvent(new CustomEvent('fabricos:open-estop')); } },
      { id: 'act:notifications', label: 'Show notifications', hint: 'Bell drawer', kind: 'action', icon: Bell, run: () => { window.dispatchEvent(new CustomEvent('fabricos:open-notifications')); } },
    ];
    return [...workspaces, ...droneItems, ...actions];
  }, [robots, navigate, select]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items
      .map((c) => ({ c, score: score(c, q) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.c);
  }, [items, query]);

  // Cmd/Ctrl+K opens.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
      setQuery('');
      setCursor(0);
    }
  }, [open]);

  useEffect(() => {
    setCursor((c) => Math.min(c, Math.max(0, results.length - 1)));
  }, [results.length]);

  function runAt(i: number) {
    const c = results[i];
    if (!c) return;
    c.run();
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(results.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runAt(cursor);
    }
  }

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="fixed left-1/2 top-[18%] z-[60] w-[min(720px,90vw)] -translate-x-1/2 overflow-hidden rounded-xl border border-border-default bg-surface-1 shadow-[var(--shadow-pop)]">
        <div className="flex items-center gap-2 border-b border-border-default px-3 py-2.5">
          <Search className="h-4 w-4 text-fg-muted" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search workspaces · drones · actions"
            className="flex-1 bg-transparent text-[14px] text-fg-primary placeholder:text-fg-muted outline-none"
          />
          <Sparkles className="h-3.5 w-3.5 text-accent" />
          <kbd className="rounded border border-border-default bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-fg-muted">
            esc
          </kbd>
        </div>

        <ul className="max-h-[55vh] overflow-y-auto py-1">
          {results.length === 0 ? (
            <li className="px-3 py-6 text-center text-[12px] text-fg-muted">
              No matches for &quot;{query}&quot;
            </li>
          ) : (
            <>
              {groupByKind(results).map(({ kind, items }) => (
                <li key={kind}>
                  <div className="px-3 pb-0.5 pt-2 font-mono text-[9.5px] uppercase tracking-[0.16em] text-fg-muted">
                    {GROUP_LABEL[kind]}
                  </div>
                  <ul>
                    {items.map((c) => {
                      const idx = results.indexOf(c);
                      const Icon = c.icon;
                      return (
                        <li key={c.id}>
                          <button
                            type="button"
                            onMouseEnter={() => setCursor(idx)}
                            onClick={() => runAt(idx)}
                            className={cn(
                              'flex w-full items-center gap-2.5 px-3 py-1.5 text-left',
                              cursor === idx ? 'bg-accent/15 text-accent' : 'text-fg-secondary hover:bg-surface-2/60',
                            )}
                          >
                            <Icon className={cn('h-4 w-4 flex-none', cursor === idx ? 'text-accent' : 'text-fg-muted')} />
                            <span className="min-w-0 flex-1">
                              <span className={cn('block truncate text-[12.5px] font-medium', cursor === idx ? 'text-accent' : 'text-fg-primary')}>
                                {c.label}
                              </span>
                              <span className="block truncate text-[10.5px] text-fg-muted">{c.hint}</span>
                            </span>
                            {cursor === idx && <CornerDownLeft className="h-3 w-3" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </>
          )}
        </ul>

        <footer className="flex items-center justify-between border-t border-border-default bg-surface-2 px-3 py-1.5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-fg-muted">
            <kbd className="rounded border border-border-default bg-surface-3 px-1.5 py-0.5">↑↓</kbd>
            navigate
            <kbd className="ml-1 rounded border border-border-default bg-surface-3 px-1.5 py-0.5">↵</kbd>
            run
          </div>
          <div className="font-mono text-[10px] text-fg-muted">
            {results.length} result{results.length !== 1 && 's'}
          </div>
        </footer>
      </div>
    </>
  );
}

type ItemKind = 'workspace' | 'fleet' | 'action';

interface CommandItem {
  id: string;
  label: string;
  hint: string;
  kind: ItemKind;
  icon: React.ComponentType<{ className?: string }>;
  run: () => void;
}

const GROUP_LABEL: Record<ItemKind, string> = {
  workspace: 'Workspaces',
  fleet: 'Fleet',
  action: 'Actions',
};

function groupByKind(items: CommandItem[]): { kind: ItemKind; items: CommandItem[] }[] {
  const buckets: Record<ItemKind, CommandItem[]> = { workspace: [], fleet: [], action: [] };
  for (const c of items) buckets[c.kind].push(c);
  const out: { kind: ItemKind; items: CommandItem[] }[] = [];
  if (buckets.workspace.length) out.push({ kind: 'workspace', items: buckets.workspace });
  if (buckets.fleet.length) out.push({ kind: 'fleet', items: buckets.fleet });
  if (buckets.action.length) out.push({ kind: 'action', items: buckets.action });
  return out;
}

function score(c: CommandItem, q: string): number {
  const hay = `${c.label} ${c.hint} ${c.id}`.toLowerCase();
  if (hay.includes(q)) {
    // Tighter match if label starts with q
    if (c.label.toLowerCase().startsWith(q)) return 5;
    if (c.label.toLowerCase().includes(q)) return 3;
    return 1;
  }
  return 0;
}
